<template>
  <div class="ccma-security-questions">
    <div v-if="isLoading" class="loading-container">
      <div class="loading-content">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ t('consultantCorner.securityQuestions.loading') }}</p>
      </div>
    </div>

    <div v-else-if="loadError" class="error-container">
      <div class="error-content">
        <i class="fas fa-exclamation-triangle"></i>
        <h5>{{ t('consultantCorner.securityQuestions.loadError') }}</h5>
        <p>{{ loadError }}</p>
        <button @click="loadSecurityQuestions" class="btn-simple">
          <span>{{ t('consultantCorner.securityQuestions.tryAgain') }}</span>
        </button>
      </div>
    </div>

    <div v-else>
      <!-- Security Questions Section -->
      <div class="security-questions-header">
        <h4>{{ t('consultantCorner.securityQuestions.header') }}</h4>
        <p class="subtitle">{{ t('consultantCorner.securityQuestions.subHeader') }}</p>
      </div>

      <div class="question-group">
        <h5>{{ t('consultantCorner.securityQuestions.question1Label') }}</h5>
        <div class="form-group">
          <label>{{ t('consultantCorner.securityQuestions.selectQuestion') }}</label>
          <DropDownList
            :data-items="predefinedQuestions"
            :value="formData.selectedQuestionNumber"
            :value-primitive="true"
            @change="onQuestionChange"
            text-field="text"
            value-field="value"
            class="form-control"
            :class="{ 'is-invalid': errors.selectedQuestionNumber }"
            :default-item="{ text: t('consultantCorner.securityQuestions.selectQuestionPlaceholder'), value: null }"
          />
          <div v-if="errors.selectedQuestionNumber" class="invalid-feedback">{{ errors.selectedQuestionNumber }}</div>
        </div>
        <div class="form-group">
          <label>{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
          <input
            v-model="formData.genericQuestionResponse"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.genericQuestionResponse }"
            :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
          />
          <div v-if="errors.genericQuestionResponse" class="invalid-feedback">{{ errors.genericQuestionResponse }}</div>
        </div>
      </div>

      <div class="question-group">
        <h5>{{ t('consultantCorner.securityQuestions.question2Label') }}</h5>
        <div class="form-group">
          <label>{{ t('consultantCorner.securityQuestions.customQuestionLabel') }}</label>
          <input
            v-model="formData.customQuestion"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.customQuestion }"
            :placeholder="t('consultantCorner.securityQuestions.customQuestionPlaceholder')"
          />
          <div v-if="errors.customQuestion" class="invalid-feedback">{{ errors.customQuestion }}</div>
        </div>
        <div class="form-group">
          <label>{{ t('consultantCorner.securityQuestions.answerLabel') }}</label>
          <input
            v-model="formData.customQuestionResponse"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.customQuestionResponse }"
            :placeholder="t('consultantCorner.securityQuestions.answerPlaceholder')"
          />
          <div v-if="errors.customQuestionResponse" class="invalid-feedback">{{ errors.customQuestionResponse }}</div>
        </div>
      </div>

      <div class="form-actions">
        <button
          @click="saveChanges"
          class="btn-update"
          :disabled="isSaving"
        >
          <span v-if="isSaving">{{ t('consultantCorner.securityQuestions.saving') }}</span>
          <span v-else>{{ t('consultantCorner.securityQuestions.save') }}</span>
        </button>
      </div>

      <!-- Success notification -->
      <div v-if="showSuccessMessage" class="success-notification">
        <i class="fas fa-check-circle"></i>
        <span>{{ t('consultantCorner.securityQuestions.saveSuccess') }}</span>
      </div>

      <!-- Error notification -->
      <div v-if="saveError" class="error-notification">
        <i class="fas fa-exclamation-triangle"></i>
        <span>{{ saveError }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { DropDownList, type DropDownListChangeEvent } from '@progress/kendo-vue-dropdowns'
import { SecurityQuestionGet } from '../../api/Security'
import { PwsSecurityResponseSave, BeeSecurityResponseGetConnector, type PwsSecurityResponseSaveRequest } from '../../api/ConsultantCorner'
import type { ISecurityQuestion, SecurityQuestionGetResponse } from '../../types/SecurityTypes'
import type { IBeeSecurityResponseGetResponse } from '../../types/ConsultantCornerTypes'

const { t } = useI18n()
const logger = useLogger()

interface SecurityQuestionDropdownItem {
  text: string
  value: number
}

const formData = ref({
  selectedQuestionNumber: null as number | null,
  genericQuestionResponse: '',
  customQuestion: '',
  customQuestionResponse: ''
})

const errors = ref<Record<string, string>>({})
const predefinedQuestions = ref<SecurityQuestionDropdownItem[]>([])
const isLoading = ref(true)
const isSaving = ref(false)
const loadError = ref('')
const saveError = ref('')
const showSuccessMessage = ref(false)

const loadSecurityQuestions = async () => {
  isLoading.value = true
  loadError.value = ''

  try {
    const response: SecurityQuestionGetResponse = await SecurityQuestionGet()

    if (response && response.items) {
      predefinedQuestions.value = response.items.map((q: ISecurityQuestion) => ({
        text: q.securityQuestion,
        value: q.questionsNumber
      }))
    }
  } catch (error) {
    logger.error('Error loading security questions:', error)
    loadError.value = t('consultantCorner.securityQuestions.loadError')
  } finally {
    isLoading.value = false
  }
}

const loadSavedSecurityResponses = async () => {
  try {
    const response: IBeeSecurityResponseGetResponse = await BeeSecurityResponseGetConnector(true)

    if (response && response.items && response.items.length > 0) {
      // Find the generic question (questionNumber > 0 for predefined questions)
      const genericQuestion = response.items.find(item => item.questionNumber && item.questionNumber > 0)
      if (genericQuestion) {
        formData.value.selectedQuestionNumber = genericQuestion.questionNumber
        formData.value.genericQuestionResponse = genericQuestion.responseText || ''
      }

      // Find the custom question (questionNumber = -1)
      const customQuestion = response.items.find(item => item.questionNumber === -1)
      if (customQuestion) {
        formData.value.customQuestion = customQuestion.customQuestion || ''
        formData.value.customQuestionResponse = customQuestion.customQuestionResponse || ''
      }
    }
  } catch (error) {
    logger.error('Error loading saved security responses:', error)
    // No need to show error to user, just log it - if there are no saved questions, that's ok
  }
}

onMounted(async () => {
  await loadSecurityQuestions()
  await loadSavedSecurityResponses()
})

const onQuestionChange = (event: DropDownListChangeEvent) => {
  formData.value.selectedQuestionNumber = event.value
  // Clear any previous error
  if (errors.value.selectedQuestionNumber) {
    delete errors.value.selectedQuestionNumber
  }
}

const validateForm = (): boolean => {
  errors.value = {}

  // Validate generic question selection
  if (!formData.value.selectedQuestionNumber) {
    errors.value.selectedQuestionNumber = t('consultantCorner.securityQuestions.questionRequired')
  }

  // Validate generic question response
  if (!formData.value.genericQuestionResponse.trim()) {
    errors.value.genericQuestionResponse = t('consultantCorner.securityQuestions.answerRequired')
  }

  // Validate custom question
  if (!formData.value.customQuestion.trim()) {
    errors.value.customQuestion = t('consultantCorner.securityQuestions.customQuestionRequired')
  }

  // Validate custom question response
  if (!formData.value.customQuestionResponse.trim()) {
    errors.value.customQuestionResponse = t('consultantCorner.securityQuestions.customAnswerRequired')
  }

  return Object.keys(errors.value).length === 0
}

const saveChanges = async () => {
  if (!validateForm()) {
    return
  }

  isSaving.value = true
  saveError.value = ''
  showSuccessMessage.value = false

  try {
    const request: PwsSecurityResponseSaveRequest = {
      GenericQuestionNumber: formData.value.selectedQuestionNumber ?? 0,
      GenericQuestionResponse: formData.value.genericQuestionResponse,
      CustomQuestion: formData.value.customQuestion,
      CustomQuestionResponse: formData.value.customQuestionResponse
    }

    await PwsSecurityResponseSave(request)

    // Show success message
    showSuccessMessage.value = true
    logger.info(t('consultantCorner.securityQuestions.saveSuccess'))

    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      showSuccessMessage.value = false
    }, 3000)

  } catch (error) {
    logger.error('Error saving security questions:', error)
    saveError.value = t('consultantCorner.securityQuestions.saveError')

    // Auto-hide error message after 5 seconds
    setTimeout(() => {
      saveError.value = ''
    }, 5000)
  } finally {
    isSaving.value = false
  }
}
</script>

<style lang="scss" scoped>
.ccma-security-questions {
  padding: $card-padding;
  max-width: 800px;
  margin: 0 auto;
}

.loading-container, .error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-content, .error-content {
  text-align: center;
  padding: 2rem;
}

.loading-content i {
  @include typography-h2;
  color: #e67e22;
  margin-bottom: 1rem;
}

.error-content i {
  @include typography-display;
  color: #dc3545;
  margin-bottom: 1rem;
}

.error-content h5 {
  color: #333;
  margin-bottom: 1rem;
}

.error-content p {
  color: #666;
  margin-bottom: 1.5rem;
}

.security-questions-header {
  margin-bottom: 2rem;
  text-align: center;
}

.security-questions-header h4 {
  @include typography-h3;
  @include font-weight-semibold;
  color: #e67e22;
  margin-bottom: 0.5rem;
}

.security-questions-header .subtitle {
  @include typography-body;
  color: #666;
  margin-bottom: 0;
}

.question-group {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.question-group h5 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e67e22;
  padding-bottom: 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  @include font-weight-semibold;
  margin-bottom: 0.5rem;
  display: block;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-md;
  transition: all 0.2s ease-in-out;
}

.form-control:focus {
  border-color: #e67e22;
  outline: none !important;
  box-shadow: none;
}

.form-control.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.invalid-feedback {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
  display: block;
}

.form-actions {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

/* Simple button styles - matching CCMARecruitmentSearch */
.btn-simple {
  padding: $spacing-2 $spacing-4;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  border-radius: $radius-sm;
  cursor: pointer;
}

.btn-simple:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.btn-simple:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-update {
  padding: $spacing-2-5 $spacing-5;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-body;
  @include font-weight-medium;
  border-radius: $radius-sm;
  cursor: pointer;
}

.btn-update:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.btn-update:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #6c757d;
}

.success-notification, .error-notification {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: $radius-md;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease-out;
}

.success-notification {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.success-notification i {
  color: #28a745;
}

.error-notification {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.error-notification i {
  color: #dc3545;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767px) {
  .ccma-security-questions {
    padding: $spacing-4;
  }

  .question-group {
    padding: 1rem;
  }

  .security-questions-header h4 {
    @include typography-h4;
  }

  .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style>
